import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const DEFAULTS = {
  theme: "dark",          // 'dark' | 'light' | 'system'
  haptics: "on",          // 'on' | 'off'
  sound: "off",           // 'on' | 'off'
  reducedMotion: "off",   // 'on' | 'off'
  largeKeys: "off",       // 'on' | 'off'
  ghostRacers: "on",      // 'on' | 'off' - practice only; never in real contests
  keyGuidance: "on",      // 'on' | 'off' - keyboard light-up; practice only
  speedUnit: "mph",       // 'mph' | 'kmh' - speedometer display unit
};

const KEY = "tr_settings";
const SettingsContext = createContext(null);

function load() {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return { ...DEFAULTS };
  }
}

function resolveTheme(theme) {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return theme;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(load);

  // Persist + apply side-effects (theme class, reduced-motion, large-keys)
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(settings));
    const root = document.documentElement;
    const isLight = resolveTheme(settings.theme) === "light";
    root.classList.toggle("light", isLight);
    root.classList.toggle("dark", !isLight);
    root.classList.toggle("reduce-motion", settings.reducedMotion === "on");
    root.classList.toggle("large-keys", settings.largeKeys === "on");
  }, [settings]);

  // React to OS theme changes when in 'system' mode
  useEffect(() => {
    if (settings.theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => document.documentElement.classList.toggle("light", mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [settings.theme]);

  const update = useCallback((key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, update }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);

// Non-hook helpers for imperative use (e.g., inside race loop)
export const hapticsEnabled = () => load().haptics === "on";
export const soundEnabled = () => load().sound === "on";
export const speedUnitPref = () => load().speedUnit || "mph";
